// WebAuthn helper for platform (biometric) authenticator.
// Uses navigator.credentials.create/get with platform authenticator and userVerification=required.

export const isBiometricSupported = () => {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
};

export async function isPlatformAuthenticatorAvailable() {
  if (!isBiometricSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

const b64urlToBuf = (b64url) => {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

const bufToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export async function registerBiometric({ challenge, userId, userName, userDisplayName, rpName = 'BioPraga OS' }) {
  const publicKey = {
    challenge: b64urlToBuf(challenge),
    rp: { name: rpName, id: window.location.hostname },
    user: {
      id: new TextEncoder().encode(userId),
      name: userName,
      displayName: userDisplayName || userName,
    },
    pubKeyCredParams: [
      { type: 'public-key', alg: -7 },   // ES256
      { type: 'public-key', alg: -257 }, // RS256
    ],
    authenticatorSelection: {
      authenticatorAttachment: 'platform',
      userVerification: 'required',
      residentKey: 'preferred',
    },
    timeout: 60000,
    attestation: 'none',
  };
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Registro biométrico cancelado.');
  return {
    credential_id: bufToB64url(cred.rawId),
    // NOTE: We don't parse attestationObject on the client for MVP.
    public_key: null,
  };
}

export async function assertBiometric({ challenge, allowedCredentials = [] }) {
  const publicKey = {
    challenge: b64urlToBuf(challenge),
    rpId: window.location.hostname,
    userVerification: 'required',
    timeout: 60000,
    allowCredentials: allowedCredentials.map((id) => ({
      type: 'public-key',
      id: b64urlToBuf(id),
      transports: ['internal'],
    })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('Autenticação biométrica cancelada.');
  return { credential_id: bufToB64url(cred.rawId) };
}

// -----------------------------------------------------------------------------
// "Remember biometric" UX helpers.
//
// Storage rationale (safe by WebAuthn design):
//   - EMAIL is user-facing, not a secret. Kept in localStorage so we can
//     pre-fill the login field even after the tab is closed.
//   - CREDENTIAL_ID is a PUBLIC identifier by WebAuthn spec — it is meant to
//     be shared with the Relying Party (server). It's NOT a token, key, or
//     secret. Kept in sessionStorage to minimize the surface exposed to XSS
//     across tabs/sessions.
//   - The actual private key never leaves the device's secure enclave; only
//     signatures produced by the authenticator ever hit our JS.
// -----------------------------------------------------------------------------
const REMEMBER_EMAIL_KEY = 'biopraga.biometric.email';
const REMEMBER_CRED_KEY = 'biopraga.biometric.credId';

const safeLocal = () => { try { return window.localStorage; } catch { return null; } };
const safeSession = () => { try { return window.sessionStorage; } catch { return null; } };

export const rememberBiometricEmail = (email, credId) => {
  const l = safeLocal(); const s = safeSession();
  if (l && email) l.setItem(REMEMBER_EMAIL_KEY, String(email));
  if (s && credId) s.setItem(REMEMBER_CRED_KEY, String(credId));
};
export const getRememberedBiometricEmail = () => {
  const l = safeLocal();
  return l ? l.getItem(REMEMBER_EMAIL_KEY) : null;
};
export const getRememberedBiometricCredId = () => {
  const s = safeSession();
  return s ? s.getItem(REMEMBER_CRED_KEY) : null;
};
export const clearRememberedBiometric = () => {
  const l = safeLocal(); const s = safeSession();
  if (l) l.removeItem(REMEMBER_EMAIL_KEY);
  if (s) s.removeItem(REMEMBER_CRED_KEY);
};
